import { LoadType } from 'types/cargosApp/Load';

type Variant = 'exchange' | 'firm';

type MockSpec = {
  rootIndex: number;
  variant: Variant;
  similarCount: number; // всего похожих (без root)
  preExpanded: number; // сколько отдаём сразу в similar_stack.stack
  firmPool?: number; // сколько разных фирм задействовать (только exchange)
};

const MOCK_SPECS: MockSpec[] = [
  { rootIndex: 1, variant: 'exchange', similarCount: 12, preExpanded: 2, firmPool: 8 },
  { rootIndex: 4, variant: 'exchange', similarCount: 5, preExpanded: 2, firmPool: 3 },
  { rootIndex: 6, variant: 'firm', similarCount: 7, preExpanded: 0 },
  { rootIndex: 8, variant: 'firm', similarCount: 4, preExpanded: 0 },
  { rootIndex: 10, variant: 'firm', similarCount: 6, preExpanded: 0 },
];

const fullSimilarByRoot = new Map<string, LoadType[]>();
let seq = 0;

const firmKey = (load: LoadType): string => {
  const firm = load.firm as { id?: string | number } | undefined;
  return firm?.id != null ? String(firm.id) : JSON.stringify(load.firm ?? {});
};

const cloneLoad = (src: LoadType, firm?: object): LoadType => {
  seq += 1;
  return {
    ...src,
    id: `mock-sim-${seq}`,
    firm: firm ?? src.firm,
    checked: false,
    lotId: undefined,
    lotNumber: undefined,
    similar: undefined,
    similarCount: undefined,
    similarStack: undefined,
  };
};

const buildFull = (spec: MockSpec, root: LoadType, donors: LoadType[]): LoadType[] => {
  const pool = spec.variant === 'exchange' ? Math.min(spec.firmPool ?? donors.length, donors.length) : donors.length;
  const out: LoadType[] = [];
  for (let i = 0; i < spec.similarCount; i += 1) {
    const donor = donors[i % Math.max(pool, 1)];
    out.push(cloneLoad(donor, spec.variant === 'firm' ? root.firm : undefined));
  }
  return out;
};

export function applySimilarStackMock(loads: LoadType[], enabled: boolean): LoadType[] {
  fullSimilarByRoot.clear();
  seq = 0;

  if (!enabled || !Array.isArray(loads) || loads.length < 12) return loads;

  const usedRoots = new Set(MOCK_SPECS.map(spec => spec.rootIndex));
  const donors = loads.filter((_, index) => !usedRoots.has(index));
  if (donors.length < 6) return loads;

  const specByIndex = new Map(MOCK_SPECS.map(spec => [spec.rootIndex, spec] as const));

  return loads.map((load, index) => {
    const spec = specByIndex.get(index);
    if (!spec) return load;

    const full = buildFull(spec, load, donors);
    fullSimilarByRoot.set(load.id, full);

    const firmsCount = spec.variant === 'firm' ? 0 : new Set(full.map(firmKey)).size;
    const stack = full.slice(0, spec.preExpanded).map(similarLoad => ({ load: similarLoad }));

    return {
      ...load,
      similarCount: spec.similarCount,
      similarStack: { firmsCount, stack },
    };
  });
}

export function getMockedSimilar(mainLoadId: string, options?: { take?: number; skip?: number }): LoadType[] | null {
  const full = fullSimilarByRoot.get(mainLoadId);
  if (!full) return null;

  const skip = options?.skip ?? 0;
  const take = options?.take ?? 3;
  return full.slice(skip, skip + take);
}
